import { PERMISSION_DENIED_MESSAGE } from '../constants/permissions';
import {
  getDenialBatchDelayMs,
  isModalSuppressed,
  normalizePermissions,
} from '../modal-suppression';
import {
  closePermissionDenied,
  openPermissionDenied,
} from '../store/permissionGateSlice';

let pendingPermissions = [];
let pendingMessage = '';
let batchTimerId = null;

function resolvePermissionsFromPayload(payload = {}) {
  if (payload.blockedPermissions?.length) {
    return normalizePermissions(payload.blockedPermissions);
  }
  if (payload.requiredPermission) {
    return normalizePermissions(payload.requiredPermission);
  }
  return [];
}

function mergePending(permissions) {
  for (const permission of permissions) {
    if (permission && !pendingPermissions.includes(permission)) {
      pendingPermissions.push(permission);
    }
  }
}

function cancelScheduledFlush() {
  if (batchTimerId != null) {
    clearTimeout(batchTimerId);
    batchTimerId = null;
  }
}

function flushPending(dispatch, getState) {
  cancelScheduledFlush();
  const permissions = normalizePermissions(pendingPermissions);
  if (!permissions.length) return false;

  const gate = getState?.()?.permissionGate;
  const combined = gate?.deniedModalOpen
    ? normalizePermissions([...(gate.blockedPermissions || []), ...permissions])
    : permissions;

  if (isModalSuppressed(combined)) {
    pendingPermissions = [];
    pendingMessage = '';
    if (gate?.deniedModalOpen) {
      dispatch(closePermissionDenied());
    }
    return false;
  }

  const message = pendingMessage || PERMISSION_DENIED_MESSAGE;
  pendingPermissions = [];
  pendingMessage = '';

  if (gate?.deniedModalOpen) {
    dispatch(
      openPermissionDenied({
        requiredPermission: permissions[0],
        blockedPermissions: permissions,
        message,
      }),
    );
    return true;
  }

  dispatch(
    openPermissionDenied({
      requiredPermission: permissions[0],
      blockedPermissions: permissions,
      message,
    }),
  );
  return true;
}

function scheduleFlush(dispatch, getState) {
  cancelScheduledFlush();
  const delayMs = getDenialBatchDelayMs(pendingPermissions);
  batchTimerId = setTimeout(() => {
    batchTimerId = null;
    flushPending(dispatch, getState);
  }, delayMs);
}

/** Clears in-flight batched denials (e.g. when the modal is dismissed). */
export function resetPermissionDeniedBatch() {
  cancelScheduledFlush();
  pendingPermissions = [];
  pendingMessage = '';
}

function dismissIfSuppressedGroup(dispatch, getState, permissions) {
  const gate = getState?.()?.permissionGate;
  if (!gate?.deniedModalOpen) return false;

  const combined = normalizePermissions([
    ...(gate.blockedPermissions || []),
    ...permissions,
  ]);
  if (!combined.length || !isModalSuppressed(combined)) return false;

  resetPermissionDeniedBatch();
  dispatch(closePermissionDenied());
  return true;
}

/**
 * Opens the permission-denied modal unless the denied permission group exactly
 * matches a stored suppression entry (order-independent).
 *
 * @param {Function} getState - `() => store.getState()`
 * @returns {boolean} false when suppressed; true when modal is/will be shown
 */
export function maybeOpenPermissionDenied(dispatch, payload, getState) {
  const options = payload || {};
  const permissions = resolvePermissionsFromPayload(options);
  if (options.message) pendingMessage = options.message;

  if (dismissIfSuppressedGroup(dispatch, getState, permissions)) {
    return false;
  }

  const gate = getState?.()?.permissionGate;
  if (gate?.deniedModalOpen) {
    const combined = normalizePermissions([
      ...(gate.blockedPermissions || []),
      ...permissions,
    ]);
    if (combined.length && isModalSuppressed(combined)) {
      resetPermissionDeniedBatch();
      dispatch(closePermissionDenied());
      return false;
    }

    dispatch(
      openPermissionDenied({
        ...options,
        blockedPermissions: permissions.length ? permissions : options.blockedPermissions,
        requiredPermission:
          options.requiredPermission || permissions[0] || '',
      }),
    );
    return true;
  }

  mergePending(permissions);
  const batched = normalizePermissions(pendingPermissions);

  if (batched.length && isModalSuppressed(batched)) {
    resetPermissionDeniedBatch();
    return false;
  }

  scheduleFlush(dispatch, getState);
  return batched.length > 0;
}
